import React, { useEffect } from 'react';
import { AlertTriangleIcon, XIcon } from 'lucide-react';
interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning' | 'default';
  onConfirm: () => void;
  onCancel: () => void;
}
export function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmText = 'Konfirmasi',
  cancelText = 'Batal',
  type = 'default',
  onConfirm,
  onCancel
}: ConfirmDialogProps) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onCancel();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onCancel]);
  if (!isOpen) return null;
  const getConfirmButtonClass = () => {
    switch (type) {
      case 'danger':
        return 'bg-[#FF3B3B] text-[#0B0F1A] hover:bg-[#FF3B3B]/90 glow-red';
      case 'warning':
        return 'bg-[#FFB800] text-[#0B0F1A] hover:bg-[#FFB800]/90 glow-amber';
      default:
        return 'bg-[#00D4FF] text-[#0B0F1A] hover:bg-[#00D4FF]/90 glow-cyan';
    }
  };
  const getIconColor = () => {
    switch (type) {
      case 'danger':
        return 'text-[#FF3B3B]';
      case 'warning':
        return 'text-[#FFB800]';
      default:
        return 'text-[#00D4FF]';
    }
  };
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-0" data-id="element-29">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-[#0B0F1A]/80 backdrop-blur-sm transition-opacity" onClick={onCancel} data-id="element-30"></div>

      {/* Dialog */}
      <div className="relative bg-[#111827] border border-[#1E3A5F] rounded-lg shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 fade-in duration-200" data-id="element-31">
        <div className="absolute inset-0 cyber-grid opacity-5 pointer-events-none" data-id="element-32"></div>

        <div className="p-6 relative z-10" data-id="element-33">
          <div className="flex items-start gap-4" data-id="element-34">
            <div className={`p-2 rounded-full bg-[#0B0F1A] border border-[#1E3A5F] ${getIconColor()}`} data-id="element-35">
              <AlertTriangleIcon className="w-6 h-6" data-id="element-36" />
            </div>
            <div className="flex-1 pt-1" data-id="element-37">
              <h3 className="text-lg font-bold text-[#E2E8F0] uppercase tracking-wide mb-2" data-id="element-38">
                {title}
              </h3>
              <p className="text-sm text-[#94A3B8] leading-relaxed" data-id="element-39">
                {message}
              </p>
            </div>
            <button onClick={onCancel} className="text-[#94A3B8] hover:text-[#E2E8F0] transition-colors" data-id="element-40">
              <XIcon className="w-5 h-5" data-id="element-41" />
            </button>
          </div>
        </div>

        <div className="bg-[#0F1629] px-6 py-4 border-t border-[#1E3A5F] flex justify-end gap-3 relative z-10" data-id="element-42">
          <button onClick={onCancel} className="px-4 py-2 border border-[#1E3A5F] text-[#E2E8F0] bg-[#1A2332] rounded hover:bg-[#1E3A5F] transition-colors font-bold text-xs uppercase" data-id="element-43">
            {cancelText}
          </button>
          <button onClick={() => {
          onConfirm();
          onCancel();
        }} className={`px-4 py-2 rounded shadow-sm font-bold text-xs uppercase transition-colors ${getConfirmButtonClass()}`} data-id="element-44">
            {confirmText}
          </button>
        </div>
      </div>
    </div>;
}